import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
} from '../../components/ui';
import { ADMIN_PENDING_SERVICE_CREDENTIALS } from '../../services/serviceService/queries';
import {
  useAdminPendingServiceCredentialsQuery,
  useVerifyProviderInsuranceMutation,
  useVerifyWorkerBackgroundCheckMutation,
  type IAdminPendingServiceCredentialsQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { serviceErrorMessage } from '../ServiceProviderPage/serviceErrors';

type Credential = IAdminPendingServiceCredentialsQuery['adminPendingServiceCredentials'][number];

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : 'Not given';

const KIND_LABELS: Record<string, string> = {
  insurance: 'Insurance',
  background_check: 'Background check',
};

const credentialKey = (c: Credential) => `${c.kind}:${c.providerId}:${c.workerCarId ?? ''}`;

/**
 * Credentials waiting for a brand admin (spec 019 W9, D15): Provider
 * insurance and worker background checks. Approving turns on the badge; a
 * verified background check also lets the worker offer handovers. The files
 * are private: there is no read link yet, so the page shows the file key to
 * find it in storage.
 */
export default function AdminServiceProvidersPage() {
  const query = useAdminPendingServiceCredentialsQuery({ fetchPolicy: 'network-only' });
  const refetchQueries = [{ query: ADMIN_PENDING_SERVICE_CREDENTIALS }];
  const [verifyInsurance] = useVerifyProviderInsuranceMutation({ refetchQueries });
  const [verifyCheck] = useVerifyWorkerBackgroundCheckMutation({ refetchQueries });
  const [busy, setBusy] = useState<string | null>(null);
  const items = query.data?.adminPendingServiceCredentials ?? [];

  const decide = async (credential: Credential, approved: boolean) => {
    const key = credentialKey(credential);
    setBusy(`${key}:${approved}`);
    try {
      if (credential.kind === 'insurance') {
        await verifyInsurance({ variables: { providerId: credential.providerId, approved } });
      } else if (credential.workerCarId) {
        await verifyCheck({
          variables: {
            workerCarId: credential.workerCarId,
            approved,
            expiresAt: credential.expiresAt ?? null,
          },
        });
      }
      toast.success(approved ? 'Approved' : 'Rejected');
    } catch (error) {
      toast.error(serviceErrorMessage(error, 'Could not save the decision'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer width="xl" canvas data-testid="admin-service-providers">
      <PageHeader
        eyebrow="Admin"
        title="Service Providers"
        description="Check each document, then approve or reject it. Approving turns on the badge customers see."
      />
      <Stack $gap={6}>
        <Card>
          <CardHeader title="Waiting for review" />
          {query.loading && !query.data ? (
            <LoadingBlock label="Loading" />
          ) : query.error && !query.data ? (
            <Alert tone="error" title="Could not load">
              {serviceErrorMessage(query.error)}
            </Alert>
          ) : items.length === 0 ? (
            <EmptyState icon={faShieldHalved} compact title="Nothing to review" />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>Type</TH>
                    <TH>Provider</TH>
                    <TH>Issued by</TH>
                    <TH>Dates</TH>
                    <TH>File</TH>
                    <TH $align="right">Decision</TH>
                  </TR>
                </THead>
                <TBody>
                  {items.map(credential => {
                    const key = credentialKey(credential);
                    return (
                      <TR key={key} data-testid={`credential-${key}`}>
                        <TD>
                          <Badge tone="info">
                            {KIND_LABELS[credential.kind] ?? credential.kind}
                          </Badge>
                        </TD>
                        <TD>
                          <div>{credential.providerName}</div>
                          {credential.workerName && (
                            <Muted $size="xs">Worker: {credential.workerName}</Muted>
                          )}
                        </TD>
                        <TD>
                          <div>{credential.issuer}</div>
                          {credential.coverageAmount != null && (
                            <Muted $size="xs">
                              Coverage ${credential.coverageAmount.toLocaleString()}
                            </Muted>
                          )}
                        </TD>
                        <TD>
                          {credential.completedAt && (
                            <div>
                              <Muted $size="xs">Done {formatDate(credential.completedAt)}</Muted>
                            </div>
                          )}
                          <Muted $size="xs">Expires {formatDate(credential.expiresAt)}</Muted>
                        </TD>
                        <TD>
                          <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                            {credential.fileKey}
                          </code>
                        </TD>
                        <TDActions>
                          <InlineRow>
                            <Button
                              type="button"
                              size="sm"
                              text="Approve"
                              isLoading={busy === `${key}:true`}
                              onClick={() => decide(credential, true)}
                              dataTestId={`credential-approve-${key}`}
                            />
                            <Button
                              type="button"
                              theme="danger"
                              size="sm"
                              text="Reject"
                              isLoading={busy === `${key}:false`}
                              onClick={() => decide(credential, false)}
                            />
                          </InlineRow>
                        </TDActions>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
